"use client";

/**
 * Server/client type-swap for an executable inline <script> that MUST run at
 * HTML-parse time — before the first paint — e.g. the notice-bar's
 * already-dismissed check that hides the bar with zero flash.
 *
 * `next/script` (afterInteractive) is wrong here: it injects post-hydration, so
 * the bar would paint and then disappear. A raw <script> child instead trips
 * React 19's "Encountered a script tag while rendering React component" on every
 * client render (React only treats module/importmap/speculationrules and
 * executable-JS types as non-exempt; a type-less/executable script is one of
 * them). This is Next's documented workaround (preventing-flash-before-hydration):
 * emit an executable type on the SERVER so the browser runs it during parse, and
 * an inert `text/plain` on the CLIENT so React sees a data block and stays quiet.
 * `suppressHydrationWarning` covers the intentional type mismatch; the script has
 * already executed from the server HTML, so the client swap never re-runs it.
 *
 * `html` MUST be a server-built constant (never unescaped user input) — this is a
 * raw inline script body.
 */
export function PrePaintScript({
  html,
  nonce,
}: {
  html: string;
  nonce?: string;
}) {
  return (
    <script
      type={typeof window === "undefined" ? "text/javascript" : "text/plain"}
      nonce={nonce}
      suppressHydrationWarning
      // Callers pass a server-built constant IIFE — no unescaped input reaches
      // the script body, so there is no XSS vector.
      // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
